import { useCallback } from 'react';

import {
  Badge,
  type VStackProps,
  VStack,
  Spinner,
  Flex,
  Error,
  ErrorHeadline,
  WarningIcon,
} from '@frontend/legacy-components';

import { getReasonText } from 'modules/common/entityActions';
import {
  ViewerAccordionPanel,
  ViewerAccordionPanelHeaderActionWrapper,
} from 'modules/common/viewer/components';
import { useDxfMappingActions } from 'modules/common/viewer/dxfViewer/mapping/mappingActions';

import { useDxfViewerSession } from '../DxfViewerContext';
import { DXF_Z_INDEX } from '../constants';
import { useDxfLayerPanel } from '../hooks/useDxfLayerPanel';
import { useDxfRotateControls } from '../hooks/useDxfRotateControls';
import { DxfIssuesPanel } from '../mapping/components/DxfIssuesPanel';
import { DxfMappingPanel } from '../mapping/components/DxfMappingPanel';
import { EditMappingButton } from '../mapping/components/EditMappingButton';
import { useDxfMappingIssueGroups } from '../mapping/hooks/useDxfMappingIssueGroups';
import { useDxfMappingPanel } from '../mapping/hooks/useDxfMappingPanel';
import { useDxfMappingSubject } from '../mapping/hooks/useDxfMappingSubject';
import { useIssueGroupSelection } from '../mapping/hooks/useIssueGroupSelection';
import type { DrawingModel, DxfViewerSession } from '../session/types';
import { useDxfLoad, useReadableStore } from '../session/useSessionState';
import { DxfLayerPanel, DXF_LAYER_PANEL_MAX_H } from './DxfLayerPanel';
import { DxfRotateControls } from './DxfRotateControls';
import { ToggleColoringButton } from './ToggleColoringButton';

type DxfLayerPanelReadyProps = VStackProps & {
  session: DxfViewerSession;
  model: DrawingModel;
};

/**
 * The side panels, for the viewer's panel column.
 *
 * Assumes a ready drawing: `DxfSidePanelHost` already gates the column on
 * `load.status`.
 */
const DxfSidePanelsReady = ({
  session,
  model,
  ...props
}: DxfLayerPanelReadyProps) => {
  const { isLayerColoringOn, onToggleLayerColoring, ...layerPanel } =
    useDxfLayerPanel(session, model);
  const { isMappingColoringOn, onToggleMappingColoring, mode, ...panel } =
    useDxfMappingPanel(session);
  const mapping = useDxfMappingSubject(session);
  const { edit } = useDxfMappingActions();
  const isEditMappingVisible = mapping !== null && edit.getIsVisible(mapping);
  const canEnterMapping = mapping !== null && edit.getCanExecute(mapping);
  const enterMappingDisabledReason =
    mapping === null
      ? undefined
      : getReasonText(edit.getDisabledReason(mapping));
  const onEnterMappingMode = useCallback(() => {
    if (mapping) {
      edit.execute(mapping);
    }
  }, [mapping, edit]);
  const showEditMapping =
    panel.hasMapping && mode !== 'mapping' && isEditMappingVisible;
  // Viewing interrogation results takes the layer and mapping panels away; the
  // rotate controls are part of the canvas and stay.
  const showLayerAndMappingPanels = mode !== 'interrogation';
  const validation = useReadableStore(session.mappingValidation);
  const issueGroups = useDxfMappingIssueGroups(session, model, validation);
  const { onSelectGroup, onHoverGroup, onSelectEntity, onHoverEntity } =
    useIssueGroupSelection(session, model);
  // The header badge is the total across every group — "the totals", not a
  // per-group count (those live on each group's own row).
  const totalIssueCount = issueGroups.reduce(
    (sum, group) => sum + group.count,
    0,
  );
  // The panel body already shows a spinner until the first check settles.
  // After that, a live re-check is the small header spinner only.
  const showPendingSpinner = validation.isInitialized && validation.isPending;
  const rotateControlsProps = useDxfRotateControls(session);
  return (
    <VStack
      data-testid="dxf-side-panel-host"
      position="absolute"
      p={2}
      top={0}
      left={0}
      bottom={0}
      zIndex={DXF_Z_INDEX.CHROME}
      spacing={0.5}
      align="stretch"
      w="17rem"
      pointerEvents="none"
      overflowY="clip"
      {...props}
    >
      {showLayerAndMappingPanels && (
        <>
          <ViewerAccordionPanel
            title="Layers"
            data-testid="dxf-layers-accordion"
            flexGrow={0}
            flexShrink={0}
            panelMaxH={DXF_LAYER_PANEL_MAX_H}
            headerAction={
              <ViewerAccordionPanelHeaderActionWrapper>
                <ToggleColoringButton
                  isColoringOn={isLayerColoringOn}
                  onToggleColoring={onToggleLayerColoring}
                  hideColoringLabel="Hide layer colors"
                  showColoringLabel="Show layer colors"
                />
              </ViewerAccordionPanelHeaderActionWrapper>
            }
          >
            <DxfLayerPanel {...layerPanel} />
          </ViewerAccordionPanel>
          <ViewerAccordionPanel
            title="Mapping"
            data-testid="dxf-mapping-accordion"
            flexGrow={0}
            flexShrink={0}
            headerActionWidth={showEditMapping ? 7 : 4}
            headerAction={
              panel.hasMapping ? (
                <ViewerAccordionPanelHeaderActionWrapper
                  spacing={showEditMapping ? 1 : 0}
                >
                  <ToggleColoringButton
                    isColoringOn={isMappingColoringOn}
                    onToggleColoring={onToggleMappingColoring}
                    hideColoringLabel="Hide mapping colors"
                    showColoringLabel="Show mapping colors"
                  />
                  {showEditMapping ? (
                    <EditMappingButton
                      isDisabled={!canEnterMapping}
                      disabledReason={enterMappingDisabledReason}
                      onEnterMappingMode={onEnterMappingMode}
                    />
                  ) : undefined}
                </ViewerAccordionPanelHeaderActionWrapper>
              ) : undefined
            }
          >
            <DxfMappingPanel
              {...panel}
              isMapping={mode === 'mapping'}
              canEnterMapping={canEnterMapping}
              enterMappingDisabledReason={enterMappingDisabledReason}
              onEnterMappingMode={onEnterMappingMode}
            />
          </ViewerAccordionPanel>
        </>
      )}

      {mode === 'mapping' && (
        <ViewerAccordionPanel
          title="Issues"
          data-testid="dxf-issues-accordion"
          flexGrow={0}
          flexShrink={1}
          minH={0}
          headerAction={
            (totalIssueCount > 0 || showPendingSpinner) && (
              <ViewerAccordionPanelHeaderActionWrapper spacing={1}>
                {showPendingSpinner && (
                  <Spinner size="sm" data-testid="dxf-issues-pending-spinner" />
                )}
                {totalIssueCount > 0 && (
                  <Badge
                    colorScheme="warning"
                    variant="subtle"
                    data-testid="dxf-issues-count-badge"
                  >
                    {totalIssueCount}
                  </Badge>
                )}
              </ViewerAccordionPanelHeaderActionWrapper>
            )
          }
        >
          {!validation.isInitialized ? (
            <Flex
              align="center"
              justify="center"
              px={4}
              py={4}
              data-testid="dxf-issues-loading"
            >
              <Spinner size="md" />
            </Flex>
          ) : validation.error !== null && issueGroups.length === 0 ? (
            <Error size="sm" px={4} py={2} data-testid="dxf-issues-error">
              <WarningIcon boxSize={3} color="alert.error" />
              <ErrorHeadline>Something went wrong.</ErrorHeadline>
            </Error>
          ) : (
            <DxfIssuesPanel
              groups={issueGroups}
              onSelectGroup={onSelectGroup}
              onHoverGroup={onHoverGroup}
              onSelectEntity={onSelectEntity}
              onHoverEntity={onHoverEntity}
            />
          )}
        </ViewerAccordionPanel>
      )}
      <DxfRotateControls
        {...rotateControlsProps}
        pointerEvents="auto"
        flexGrow={0}
        flexShrink={0}
        mt="auto"
      />
    </VStack>
  );
};
DxfSidePanelsReady.displayName = 'DxfSidePanelsReady';

/**
 * Load gate for the side panels. Pages pass positioning only — they do not
 * branch on `session.load`.
 */
export const DxfSidePanelsHost = (props: VStackProps) => {
  const session = useDxfViewerSession();
  const load = useDxfLoad(session);
  if (load.status !== 'ready') {
    return null;
  }
  return <DxfSidePanelsReady session={session} model={load.model} {...props} />;
};
DxfSidePanelsHost.displayName = 'DxfSidePanelsHost';
